'use client';

import React, { useCallback, useEffect, useRef, useState } from 'react';
import clsx from 'clsx';

interface EditableHeadingProps {
  value: string;
  onCommit: (next: string) => void;
  className?: string;
  title?: string;
}

/**
 * Text that becomes an inline input on double-click. Enter/blur commits,
 * Escape cancels. Lives outside components/entities/pages so it can hold the
 * small edit state (the entity-page lint rule forbids useState there).
 */
export function EditableHeading({ value, onCommit, className, title }: EditableHeadingProps) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (editing) {
      inputRef.current?.focus();
      inputRef.current?.select();
    }
  }, [editing]);

  const begin = useCallback(() => {
    setDraft(value);
    setEditing(true);
  }, [value]);
  const commit = useCallback(() => {
    const next = draft.trim();
    if (next && next !== value) onCommit(next);
    setEditing(false);
  }, [draft, value, onCommit]);
  const cancel = useCallback(() => setEditing(false), []);

  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => setDraft(e.target.value), []);
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        commit();
      } else if (e.key === 'Escape') {
        e.preventDefault();
        cancel();
      }
    },
    [commit, cancel],
  );

  if (editing) {
    return (
      <input
        ref={inputRef}
        value={draft}
        onChange={handleChange}
        onBlur={commit}
        onKeyDown={handleKeyDown}
        className={clsx('w-full min-w-0 rounded-md bg-white/[0.04] px-1 outline-none ring-1 ring-white/15', className)}
      />
    );
  }

  return (
    <span onDoubleClick={begin} title={title ?? 'Double-click to rename'} className={clsx('cursor-text', className)}>
      {value}
    </span>
  );
}
